import { useMemo, useState } from 'react'
import type { Player, Position } from '@/game/types'
import { PlayerCard } from './PlayerCard'

const POSITION_ORDER: Position[] = ['FB', 'HB', 'C', 'HF', 'F', 'FOL', 'INT', 'EMG']
const POSITION_LABELS: Record<Position, string> = {
  FB: 'Full Back',
  HB: 'Half Back',
  C: 'Centre',
  HF: 'Half Forward',
  F: 'Forward',
  FOL: 'Follower',
  INT: 'Interchange',
  EMG: 'Emergency',
}

interface SquadScreenProps {
  players: Player[]
  development: { playerId: string; focus: string; points: number; isLocked: boolean }[]
  onPlayerClick: (player: Player) => void
  onBack: () => void
}

export function SquadScreen({ players, development, onPlayerClick, onBack }: SquadScreenProps) {
  const [filter, setFilter] = useState<Position | 'all'>('all')
  const [sort, setSort] = useState<'name' | 'age' | 'fitness' | 'form' | 'morale' | 'position'>('position')

  const filtered = useMemo(() => {
    let result = [...players]
    if (filter !== 'all') {
      result = result.filter(p => p.position === filter)
    }
    result.sort((a, b) => {
      switch (sort) {
        case 'name': return a.name.localeCompare(b.name)
        case 'age': return a.age - b.age
        case 'fitness': return b.fitness - a.fitness
        case 'form': return b.form - a.form
        case 'morale': return b.morale - a.morale
        case 'position':
        default: {
          const ai = POSITION_ORDER.indexOf(a.position)
          const bi = POSITION_ORDER.indexOf(b.position)
          if (ai !== bi) return ai - bi
          return a.name.localeCompare(b.name)
        }
      }
    })
    return result
  }, [players, filter, sort])

  const devMap = useMemo(() => new Map(development.map(d => [d.playerId, d.points])), [development])

  const renderFace = (player: Player) => {
    const v = player.visual
    if (!v) return <svg viewBox="0 0 100 100" width="80" height="80"><circle cx="50" cy="50" r="45" fill="var(--gold)" opacity="0.2"/><text x="50" y="55" textAnchor="middle" fill="var(--gold)" fontSize="24" fontWeight="700">{player.name.charAt(0)}</text></svg>
    return (
      <svg viewBox="0 0 100 100" width="80" height="80" role="img" aria-label={v.description}>
        <defs>
          <radialGradient id={`face-${player.id}`} cx="50%" cy="30%" r="50%">
            <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.15"/>
            <stop offset="70%" stopColor="var(--panel-2)" stopOpacity="1"/>
          </radialGradient>
        </defs>
        <circle cx="50" cy="35" r="30" fill={`url(#face-${player.id})`} />
        <ellipse cx="40" cy="30" rx="6" ry="5" fill="var(--gold)" opacity="0.3"/>
        <ellipse cx="60" cy="30" rx="6" ry="5" fill="var(--gold)" opacity="0.3"/>
        <path d="M35 50 Q50 58 65 50" stroke="var(--gold)" strokeWidth="2" fill="none" opacity="0.4"/>
        <text x="50" y="85" textAnchor="middle" fill="var(--gold)" fontSize="10" fontWeight="700" fontFamily="Barlow Condensed">{v.traits.slice(0,2).join(' · ')}</text>
      </svg>
    )
  }

  return (
    <div className="screen">
      <button className="back-link" type="button" onClick={onBack}>Back to Week</button>
      <div className="page-title">
        <div>
          <h1>Squad</h1>
          <p>{filtered.length} of {players.length} players · {development.reduce((s,d)=>s+d.points,0)} development points allocated</p>
        </div>
      </div>

      <div className="panel" style={{padding: '16px 20px', marginBottom: '16px'}}>
        <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center'}}>
          <label style={{fontSize: '11px', color: 'var(--dim)', textTransform: 'uppercase', marginRight: '8px'}}>
            Filter by position
            <select value={filter} onChange={e => setFilter(e.target.value as Position | 'all')} style={{marginLeft: '8px', padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--ink)', fontSize: '12px'}}>
              <option value="all">All</option>
              {POSITION_ORDER.map(pos => <option key={pos} value={pos}>{POSITION_LABELS[pos]}</option>)}
            </select>
          </label>
          <label style={{fontSize: '11px', color: 'var(--dim)', textTransform: 'uppercase', marginLeft: '20px'}}>
            Sort
            <select value={sort} onChange={e => setSort(e.target.value as typeof sort)} style={{marginLeft: '8px', padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--ink)', fontSize: '12px'}}>
              <option value="position">Position</option>
              <option value="name">Name</option>
              <option value="age">Age</option>
              <option value="fitness">Fitness</option>
              <option value="form">Form</option>
              <option value="morale">Morale</option>
            </select>
          </label>
        </div>
      </div>

      <div className="squad-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px'}}>
        {filtered.map(player => {
          const dev = devMap.get(player.id) ?? 0
          return (
            <div key={player.id} style={{position: 'relative'}}>
              <PlayerCard isFlipped={false} onFlip={() => onPlayerClick(player)}>
                <div className="player-card-front">
                  <div className="player-card-face">
                    {renderFace(player)}
                    <div className="player-card-name">{player.name}</div>
                    <div className="player-card-role">{player.primaryRole}</div>
                    <div className="player-card-position">{POSITION_LABELS[player.position]}</div>
                    {dev > 0 && (
                      <div className="status-pill" style={{marginTop: '8px', fontSize: '10px'}}>+{dev} dev</div>
                    )}
                    {player.isSelected && (
                      <div className="status-pill" style={{marginTop: '8px', fontSize: '10px', background: 'var(--good)', borderColor: '#276b63', color: 'var(--good)'}}>Selected</div>
                    )}
                    {player.injury && (
                      <div className="status-pill injury-pill" style={{marginTop: '8px', fontSize: '10px'}}>{player.injury.severity} · {player.injury.remainingWeeks}w</div>
                    )}
                    {!player.isSelected && player.position !== 'EMG' && (
                      <div className="status-pill" style={{marginTop: '8px', fontSize: '10px', background: 'var(--panel-2)', borderColor: 'var(--line)', color: 'var(--muted)'}}>Not selected</div>
                    )}
                  </div>
                  <div style={{display: 'flex', gap: '12px', justifyContent: 'center', fontSize: '10px', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '.08em'}}>
                    <span>Age {player.age}</span>
                    <span>Fitness {player.fitness}</span>
                    <span>Form {player.form}</span>
                  </div>
                </div>
                <div className="player-card-back">
                  <h3>{player.name}</h3>
                  <div className="player-card-back-grid">
                    <div><span className="player-card-back-label">Age</span><span className="player-card-back-value">{player.age}</span></div>
                    <div><span className="player-card-back-label">Position</span><span className="player-card-back-value">{POSITION_LABELS[player.position]}</span></div>
                    <div><span className="player-card-back-label">Role</span><span className="player-card-back-value">{player.primaryRole}</span></div>
                    <div><span className="player-card-back-label">Contract</span><span className="player-card-back-value">{player.contractYears} years</span></div>
                    <div><span className="player-card-back-label">Fitness</span><span className="player-card-back-value">{player.fitness}</span></div>
                    <div><span className="player-card-back-label">Fatigue</span><span className="player-card-back-value">{player.fatigue}</span></div>
                    <div><span className="player-card-back-label">Availability</span><span className="player-card-back-value">{player.injury ? `${player.injury.severity} ${player.injury.type} — ${player.injury.remainingWeeks} ${player.injury.remainingWeeks === 1 ? 'week' : 'weeks'}` : 'Available'}</span></div>
                    <div><span className="player-card-back-label">Form</span><span className="player-card-back-value">{player.form}</span></div>
                    <div><span className="player-card-back-label">Morale</span><span className="player-card-back-value">{player.morale}</span></div>
                    <div><span className="player-card-back-label">Dev. Focus</span><span className="player-card-back-value">{player.developmentFocus ?? 'None'}</span></div>
                    <div><span className="player-card-back-label">Dev. Points</span><span className="player-card-back-value">{dev}</span></div>
                    <div><span className="player-card-back-label">Selected</span><span className="player-card-back-value">{player.isSelected ? 'Yes' : 'No'}</span></div>
                    <div><span className="player-card-back-label">Position in Team</span><span className="player-card-back-value">{player.positionInTeam ? POSITION_LABELS[player.positionInTeam] : 'None'}</span></div>
                    <div><span className="player-card-back-label">Role in Team</span><span className="player-card-back-value">{player.roleInTeam ?? 'None'}</span></div>
                  </div>
                  {player.visual && (
                    <div className="player-card-visual">
                      <strong>Visual: </strong>{player.visual.description}
                      <div className="player-card-traits">
                        {player.visual.traits.map(t => <span key={t} className="player-card-trait">{t}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              </PlayerCard>
            </div>
          )
        })}
      </div>
      <div className="sticky-actions"><span>Squad review complete</span><button className="primary-action" type="button" onClick={onBack}>Return to Week</button></div>
    </div>
  )
}